import type { PricingModel } from '@weft/types';
import { FIXED_PRODUCT_TYPES } from '@weft/types';
import type { Optional } from '@weft/utils';
import { isNotNullOrUndefined, isNullOrUndefined, isSingleItem, raise } from '@weft/utils';
import { isEmptyArray, every } from '@weft/utils';
import { isCommercialTermsProductVariant,
  isCommissionProductVariant,
  isFixedPerUnitProductVariant,
  isInstallmentsProductVariant,
  isOneTimeProductVariant,
  isPerUnitProductVariant,
  isRefundProductVariant,
  isSubscriptionProductVariant, isTieredSubscriptionProductVariant,
  isTiersProductVariant,
  isTiersVolumeProductVariant } from '../product-variants-filter';
import { FIXED_PRICING_MODELS, USAGE_PRICING_MODELS } from './products-filter-consts';
import type { FilterableProduct } from './products-filter-types/filterable-product';
import type { FilterableFixedProduct } from './products-filter-types/filterable-product/filterable-products/filterable-fixed-product';
import type { FilterableUsageProduct } from './products-filter-types/filterable-product/filterable-products/filterable-usage-product';
import type { CommissionProduct,
  FixedPerUnitProduct,
  OneTimeProduct,
  SubscriptionProduct,
  LineItemCreditsProduct } from './products-filter-types/pricing-product';
import type { CommercialTermsProduct } from './products-filter-types/pricing-product/pricing-products/fixed-product/fixed-products/commercial-terms-product';
import type { InstallmentsProduct } from './products-filter-types/pricing-product/pricing-products/fixed-product/fixed-products/installments-product';
import type { RefundProduct } from './products-filter-types/pricing-product/pricing-products/fixed-product/fixed-products/refund-product';
import type { ProductType } from './products-filter-types/product-type';
import { PRODUCT_TYPES } from './products-filter-types/product-type';

export function isSubscriptionProduct(product: FilterableProduct): product is SubscriptionProduct {
  return isSingleItem(product.variants) && isSubscriptionProductVariant(product.variants[0]);
}

export function isOneTimeProduct(product: FilterableProduct): product is OneTimeProduct {
  return isSingleItem(product.variants) && isOneTimeProductVariant(product.variants[0]);
}

export function isFixedPerUnitProduct(product: FilterableProduct): product is FixedPerUnitProduct {
  return isSingleItem(product.variants) && isFixedPerUnitProductVariant(product.variants[0]);
}

export function isCommercialTermsProduct(product: FilterableProduct): product is CommercialTermsProduct {
  return isSingleItem(product.variants) && isCommercialTermsProductVariant(product.variants[0]);
}

export function isInstallmentsProduct(product: FilterableProduct): product is InstallmentsProduct {
  return isSingleItem(product.variants) && isInstallmentsProductVariant(product.variants[0]);
}

export function isCreditProduct(product: FilterableProduct): product is LineItemCreditsProduct {
  return isOneTimeProduct(product) && product.variants.some(({ settings }) => isNotNullOrUndefined(settings) && settings.productType === FIXED_PRODUCT_TYPES.Credit);
}

export function isRefundProduct(product: FilterableProduct): product is RefundProduct {
  return isSingleItem(product.variants) && isRefundProductVariant(product.variants[0]);
}

export function filterCommercialTermsProduct(products: FilterableProduct[]) {
  const commercialTermsProducts = products.filter(isCommercialTermsProduct);

  if (commercialTermsProducts.length > 1) {
    raise('Multiple commercial terms products');
  }

  return isEmptyArray(commercialTermsProducts) ? undefined : commercialTermsProducts[0];
}

export function filterSubscriptionProducts(products: FilterableProduct[]) {
  return products.filter(isSubscriptionProduct);
}

export function isCommissionProduct(product: FilterableProduct): product is CommissionProduct {
  return isSingleItem(product.variants) && isCommissionProductVariant(product.variants[0]);
}

export function filterCommissionProducts(products: FilterableProduct[]) {
  return products.filter(isCommissionProduct);
}

export function filterInstallmentsProducts(products: FilterableProduct[]) {
  return products.filter(isInstallmentsProduct);
}

export function filterUsageProducts<
  ResultUsageProduct extends FilterableUsageProduct,
>(products: Optional<FilterableProduct[]>) {
  return isNullOrUndefined(products) ? [] : filterProductByType<ResultUsageProduct>(products, PRODUCT_TYPES.USAGE);
}

export function filterFixedProducts<
  ResultFixedProduct extends FilterableFixedProduct,
>(products: FilterableProduct[] | undefined) {
  return isNullOrUndefined(products) ? [] : filterProductByType<ResultFixedProduct>(products, PRODUCT_TYPES.FIXED);
}

export function filterOneTimeProducts(products: FilterableProduct[]) {
  return products.filter(isOneTimeProduct);
}

export function filterFixedPerUnitProducts(products: FilterableProduct[]) {
  return products.filter(isFixedPerUnitProduct);
}

export function isUsageProduct<UsageProductType extends FilterableUsageProduct>(product: Optional<FilterableProduct>): product is UsageProductType {
  return isOfProductType(product, PRODUCT_TYPES.USAGE);
}

export function isFixedProduct<FixedProductType extends FilterableFixedProduct = FilterableFixedProduct>(product: Optional<FilterableProduct>): product is FixedProductType {
  return isNotNullOrUndefined(product) && isOfProductType<FixedProductType>(product, PRODUCT_TYPES.FIXED) && isSingleItem(product.variants);
}

export function isTiersUsageProductOnly<UsageProductType extends FilterableUsageProduct>(product: Optional<FilterableProduct>): product is UsageProductType {
  return isOfProductType(product, PRODUCT_TYPES.USAGE) && every(product.variants, isTiersProductVariant);
}

export function isTieredSubscriptionUsageProductOnly<UsageProductType extends FilterableUsageProduct>(product: Optional<FilterableProduct>): product is UsageProductType {
  return isOfProductType(product, PRODUCT_TYPES.USAGE) && every(product.variants, isTieredSubscriptionProductVariant);
}

export function isPerUnitUsageProductOnly<UsageProductType extends FilterableUsageProduct>(product: FilterableProduct): product is UsageProductType {
  return isOfProductType(product, PRODUCT_TYPES.USAGE) && every(product.variants, isPerUnitProductVariant);
}

export function isFixedOneTimeProductOnly<FixedProductType extends FilterableFixedProduct>(product: FilterableProduct): product is FixedProductType {
  return isOfProductType(product, PRODUCT_TYPES.FIXED) && every(product.variants, isOneTimeProductVariant);
}

export function isFixedPerUnitProductOnly<FixedProductType extends FilterableFixedProduct>(product: FilterableProduct): product is FixedProductType {
  return isOfProductType(product, PRODUCT_TYPES.FIXED) && every(product.variants, isFixedPerUnitProductVariant);
}

export function isSubscriptionProductOnly<SubscriptionProductType extends FilterableProduct>(product: FilterableProduct): product is SubscriptionProductType {
  return isOfProductType(product, PRODUCT_TYPES.FIXED) && every(product.variants, isSubscriptionProductVariant);
}

export function isCommercialTermsProductOnly<CommercialTermsProductType extends FilterableProduct>(product: FilterableProduct): product is CommercialTermsProductType {
  return isOfProductType(product, PRODUCT_TYPES.FIXED) && every(product.variants, isCommercialTermsProductVariant);
}

export function isRefundProductOnly<RefundProductType extends FilterableProduct>(product: FilterableProduct): product is RefundProductType {
  return isOfProductType(product, PRODUCT_TYPES.FIXED) && every(product.variants, isRefundProductVariant);
}

export function isInstallmentsProductOnly<InstallmentsProductType extends FilterableProduct>(product: FilterableProduct): product is InstallmentsProductType {
  return isOfProductType(product, PRODUCT_TYPES.FIXED) && every(product.variants, isInstallmentsProductVariant);
}

export function isCommissionProductOnly<CommissionProductType extends FilterableProduct>(product: FilterableProduct): product is CommissionProductType {
  return isOfProductType(product, PRODUCT_TYPES.FIXED) && every(product.variants, isCommissionProductVariant);
}

export function isSingleVariantProduct(product: FilterableUsageProduct) {
  return isSingleItem(product.variants);
}
export function getProductType(product: FilterableProduct) {
  const variantsProductTypes =
    product.variants!.map(
      ({ pricing }) => getProductTypeFromPricingModel(pricing.pricingModel!),
    );

  const uniqueVariantsProductTypes = Array.from(new Set(variantsProductTypes));

  if (isEmptyArray(uniqueVariantsProductTypes)) {
    throw new Error('Empty variants');
  }

  if (uniqueVariantsProductTypes.length > 1) {
    throw new Error('Heterogeneous Variants');
  }

  return uniqueVariantsProductTypes[0];
}

export function filterCommitmentProducts<Product extends FilterableProduct = FilterableProduct>(products: FilterableProduct[]) {
  return products.filter((product): product is Product => isCommitmentProduct(product));
}

export function filterOutCommissionProducts<Product extends FilterableProduct = FilterableProduct>(products: FilterableProduct[]) {
  return products.filter((product): product is Product => !isCommissionProduct(product));
}

export function getCommitmentUsageProducts<Product extends FilterableUsageProduct>(products: FilterableProduct[]) {
  return products.filter<Product>(isCommitmentUsageProduct);
}

export function isNonCommitmentUsageProduct(product: FilterableProduct) {
  return !isCommitmentUsageProduct(product);
}

export function isCommitmentUsageProduct<CommitmentUsageProduct extends FilterableUsageProduct>(product: FilterableProduct): product is CommitmentUsageProduct {
  return isUsageProduct(product) && isCommitmentProduct<CommitmentUsageProduct>(product);
}

export function isCommitmentProduct<CommitmentProduct extends FilterableProduct>(product: Optional<FilterableProduct>): product is CommitmentProduct {
  return isNotNullOrUndefined(product?.commitment);
}

export function getFixedProductPricingModel(product: FilterableFixedProduct) {
  return product.variants[0].pricing.pricingModel;
}

function getProductTypeFromPricingModel(pricingModel: PricingModel) {
  if (FIXED_PRICING_MODELS.includes(pricingModel)) {
    return PRODUCT_TYPES.FIXED;
  }

  if (USAGE_PRICING_MODELS.includes(pricingModel)) {
    return PRODUCT_TYPES.USAGE;
  }

  throw new Error('Unknown pricing model');
}

function filterProductByType<ResultProduct extends FilterableProduct>(products: FilterableProduct[], type: ProductType) {
  return products.filter((product): product is ResultProduct => isOfProductType<ResultProduct>(product, type));
}

function isOfProductType<ResultProduct extends FilterableProduct = FilterableProduct>(product: Optional<FilterableProduct>, type: ProductType): product is ResultProduct {
  return isNotNullOrUndefined(product) && getProductType(product) === type;
}

export function isTiersVolumeProduct<UsageProductType extends FilterableUsageProduct>(product: Optional<FilterableProduct>): product is UsageProductType {
  return isTiersUsageProductOnly(product) && every(product.variants, isTiersVolumeProductVariant);
}